import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Seo from '../components/Seo';
import { LedgerDashboard, FernwegStore, SurgeApp, ConceptModal } from '../components/designshowcase';

/* ─── PALETTE — solid colors only, no gradients ─────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';

/* ─────────────────────────────────────────────────────────────────
   IMAGES — reusing URLs already verified live elsewhere on the site
───────────────────────────────────────────────────────────────── */
const IMG_HERO       = 'https://images.unsplash.com/photo-1605379399642-870262d3d051?q=80&w=1600&auto=format&fit=crop';
const IMG_WEB         = 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=900&auto=format&fit=crop';
const IMG_WEBAPP      = 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=900&auto=format&fit=crop';
const IMG_MOBILE      = 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=900&auto=format&fit=crop';
const IMG_UIUX        = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?q=80&w=900&auto=format&fit=crop';
const IMG_ECOMMERCE   = 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?q=80&w=900&auto=format&fit=crop';
const IMG_API         = 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=900&auto=format&fit=crop';
const IMG_TEAM        = 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=1000&auto=format&fit=crop';
const IMG_PROCESS     = 'https://images.unsplash.com/photo-1523289333742-be1143f6b766?q=80&w=1000&auto=format&fit=crop';

/* ─────────────────────────────────────────────────────────────────
   SCROLL HELPERS — vanilla, no extra deps
───────────────────────────────────────────────────────────────── */
const useScrollY = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => { setScrollY(window.scrollY); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrollY;
};

/* Parallax offset for an individual element — moves relative to its
   own position in the viewport, used to drift card/section images. */
const useElementParallax = (speed = 0.12) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    let ticking = false;
    const update = () => {
      const el = ref.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const elCenter = rect.top + rect.height / 2;
        setOffset((elCenter - viewportCenter) * speed);
      }
      ticking = false;
    };
    const onScroll = () => { if (!ticking) { window.requestAnimationFrame(update); ticking = true; } };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, [speed]);
  return [ref, offset];
};

const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); io.unobserve(el); }
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* variant: 'up' | 'scale' | 'left' | 'right' | 'rotate' */
const Reveal = ({ children, delay = 0, variant = 'up', style = {}, className = '' }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`svc-rv svc-rv-${variant} ${visible ? 'svc-rv-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </div>
  );
};

/* Image that pans slowly inside its frame as the page scrolls */
const ParallaxImage = ({ src, alt, speed = 0.12, className = '' }) => {
  const [ref, offset] = useElementParallax(speed);
  return (
    <div ref={ref} className={`svc-pimg ${className}`}>
      <img src={src} alt={alt} style={{ transform: `translateY(${offset}px) scale(1.18)` }} loading="lazy" />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ICONS
───────────────────────────────────────────────────────────────── */
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────────
   SERVICE DATA
───────────────────────────────────────────────────────────────── */
const SERVICES = [
  {
    key: 'web',
    label: 'Website Development',
    image: IMG_WEB,
    tagline: 'Business websites, landing pages & portfolios that load fast and convert.',
    deliverables: ['Fully responsive design', 'SEO-friendly structure', 'Contact forms & CMS-ready sections', 'Hosting & deployment included'],
  },
  {
    key: 'webapp',
    label: 'Web Application',
    image: IMG_WEBAPP,
    tagline: 'Custom dashboards, SaaS tools & internal systems built around your workflow.',
    deliverables: ['Authentication & role-based access', 'Custom admin dashboards', 'Third-party API integrations', 'Scalable backend architecture'],
  },
  {
    key: 'mobile',
    label: 'Mobile App Development',
    image: IMG_MOBILE,
    tagline: 'Cross-platform apps for Android & iOS from a single codebase.',
    deliverables: ['One codebase, both platforms', 'Push notifications & offline support', 'Play Store / App Store submission', 'Clean, native-feeling UI'],
  },
  {
    key: 'uiux',
    label: 'UI/UX Design',
    image: IMG_UIUX,
    tagline: 'Wireframes, prototypes & polished interfaces designed before a single line of code.',
    deliverables: ['User research & wireframes', 'High-fidelity clickable prototype', 'Design system (colors, type, components)', 'Handoff-ready files for developers'],
  },
  {
    key: 'ecommerce',
    label: 'E-Commerce Solutions',
    image: IMG_ECOMMERCE,
    tagline: 'Online stores with secure payments, inventory, and order management built in.',
    deliverables: ['Product catalog & cart', 'Secure payment gateway integration', 'Order & inventory management', 'Admin panel to manage the store'],
  },
  {
    key: 'api',
    label: 'Custom Software & APIs',
    image: IMG_API,
    tagline: 'Backend systems and APIs that connect your tools, data, and teams.',
    deliverables: ['Custom REST API design & docs', 'Database design & optimization', 'Third-party integrations', 'Ongoing maintenance & support'],
  },
];

const REAL_PROJECTS = [
  {
    name: 'Forge Fitness',
    tag: 'Gym & Fitness Website',
    desc: 'A bold, high-energy business site for a gym brand — built to turn visitors into members.',
    url: 'https://thiranx-gym.netlify.app/',
    accent: '#FF4D2E',
  },
  {
    name: 'Solace',
    tag: 'Furniture Studio',
    desc: 'A calm, editorial storefront for a furniture studio, built around clean product presentation.',
    url: 'https://project-solace28.netlify.app/',
    accent: '#8B6F4E',
  },
  {
    name: 'Veloce GT',
    tag: '3D Car Configurator',
    desc: 'An interactive 3D configurator letting visitors rotate, customize, and explore a car in real time.',
    url: 'https://thiranxcar-model28.netlify.app/',
    accent: '#00C2D1',
  },
];

const CONCEPTS = [
  { key: 'ledger', Comp: LedgerDashboard, name: 'Ledger', desc: 'A B2B analytics dashboard — dense data, calm hierarchy.', variant: 'browser' },
  { key: 'fernweg', Comp: FernwegStore, name: 'Fernweg', desc: 'A boutique storefront — warm, editorial, product-led.', variant: 'browser' },
  { key: 'surge', Comp: SurgeApp, name: 'Surge', desc: 'A fitness app screen — bold, energetic, mobile-first.', variant: 'phone' },
];

const WHY_US = [
  { title: 'Certified Developers', desc: 'Built by developers trained and certified through our own courses and internships — vetted quality, not a gamble.', image: IMG_WEB },
  { title: 'Direct Communication', desc: 'You talk directly with the team building your project — no account managers, no middlemen.', image: IMG_WEBAPP },
  { title: 'Transparent Pricing', desc: 'One clear quote upfront based on your project scope. No surprise costs after we start.', image: IMG_MOBILE },
];

const PROCESS = [
  { num: '01', title: 'Share Your Idea', desc: 'Tell us what you need — a business site, an internal tool, or a mobile app. No idea is too rough.' },
  { num: '02', title: 'Get a Free Quote', desc: 'We scope the project and send back a clear price and timeline — no hidden costs.' },
  { num: '03', title: 'We Build It', desc: 'Our team designs and develops your project with regular updates along the way.' },
  { num: '04', title: 'You Launch', desc: 'You get the finished, deployed product — plus guidance on how to maintain it.' },
];

const BUDGET_OPTIONS = ['Under ₹10,000', '₹10,000 – ₹30,000', '₹30,000 – ₹75,000', '₹75,000+', 'Not sure yet'];
const TIMELINE_OPTIONS = ['ASAP', '2–4 weeks', '1–2 months', 'Flexible'];

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */
const Services = () => {
  const scrollY = useScrollY();
  const [form, setForm] = useState({ projectType: 'Website', description: '', budget: '', timeline: '', name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openConcept, setOpenConcept] = useState(null); // 'ledger' | 'fernweg' | 'surge' | null
  const formRef = useRef(null);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error('Please fill in your name, email, and a short project description.');
      return;
    }
    setSubmitting(true);
    try {
      const message = [
        `Project Type: ${form.projectType}`,
        `Budget: ${form.budget || 'Not specified'}`,
        `Timeline: ${form.timeline || 'Not specified'}`,
        form.phone ? `Phone: ${form.phone}` : null,
        '',
        'Description:',
        form.description,
      ].filter(Boolean).join('\n');

      await API.post('/contact', {
        name: form.name,
        email: form.email,
        subject: `New Freelance Project Inquiry — ${form.projectType}`,
        message,
      });
      setSubmitted(true);
      toast.success('Quote request sent! We\'ll get back to you within 24 hours.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="svc-page">
      <Seo
        title="Freelance Development Services"
        description="ThiranX builds custom websites, web applications, and mobile apps. Share your idea and get a free quote from our certified development team."
        path="/services"
      />

      {/* ══════════ HERO — full-bleed parallax image ══════════ */}
      <section className="svc-hero">
        <div className="svc-hero-bg" style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.08)`, backgroundImage: `url(${IMG_HERO})` }} />
        <div className="svc-hero-overlay" />
        <div className="svc-hero-vignette" />
        <div className="svc-hero-content">
          <div className="section-label" style={{ color: V2 }}>THIRANX FREELANCE SERVICES</div>
          <h1 className="svc-hero-title">Have an idea?<br />We'll build it.</h1>
          <p className="svc-hero-sub">
            Our certified development team builds websites, web applications, and mobile apps —
            from your first sketch to a fully launched product.
          </p>
          <button className="svc-btn-primary" onClick={scrollToForm}>
            Get a Free Quote <ArrowIcon />
          </button>
        </div>
        <div className="svc-scroll-hint">
          <span>Scroll</span>
          <div className="svc-scroll-line" />
        </div>
        <div className="svc-hero-divider" />
      </section>

      {/* ══════════ SERVICES — parallax image cards ══════════ */}
      <section className="svc-section">
        <div className="container">
          <Reveal variant="up">
            <div className="section-label">WHAT WE BUILD</div>
            <h2 className="svc-section-title">One team, every specialty you need.</h2>
          </Reveal>

          <div className="svc-cards-grid">
            {SERVICES.map((s, i) => {
              const variants = ['up', 'scale', 'rotate'];
              return (
                <Reveal key={s.key} variant={variants[i % variants.length]} delay={(i % 3) * 120}>
                  <div className="svc-card">
                    <ParallaxImage src={s.image} alt={s.label} speed={0.08} className="svc-card-img" />
                    <div className="svc-card-body">
                      <h3>{s.label}</h3>
                      <p className="svc-card-tagline">{s.tagline}</p>
                      <ul className="svc-deliverables">
                        {s.deliverables.map(d => (
                          <li key={d}><span className="svc-check"><CheckIcon /></span>{d}</li>
                        ))}
                      </ul>
                      <button className="svc-btn-outline" onClick={scrollToForm}>
                        Start This Project <ArrowIcon />
                      </button>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════ WHY THIRANX — alternating rows ══════════ */}
      <section className="svc-section svc-section-alt">
        <div className="container">
          <Reveal variant="up">
            <div className="section-label">WHY THIRANX</div>
            <h2 className="svc-section-title">Not a random freelancer. A trained, certified team.</h2>
          </Reveal>

          <div className="svc-why-rows">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} variant={i % 2 === 0 ? 'left' : 'right'} className={`svc-why-row ${i % 2 === 1 ? 'svc-why-row-rev' : ''}`}>
                <ParallaxImage src={w.image} alt={w.title} speed={0.1} className="svc-why-img" />
                <div className="svc-why-text">
                  <span className="svc-why-num">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{w.title}</h3>
                  <p>{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ RECENT WORK — real, live, deployed projects ══════════ */}
      <section className="svc-section">
        <div className="container">
          <Reveal variant="up">
            <div className="section-label">RECENT WORK</div>
            <h2 className="svc-section-title">Live projects, not mockups — see them for yourself.</h2>
            <p className="svc-section-sub">
              A few things our team has actually built and shipped. Click through to any of them — these are real,
              working sites, not screenshots.
            </p>
          </Reveal>

          <div className="svc-project-grid">
            {REAL_PROJECTS.map((p, i) => (
              <Reveal key={p.name} variant={i === 0 ? 'left' : i === 1 ? 'up' : 'right'} delay={i * 120}>
                <a className="svc-project-card" href={p.url} target="_blank" rel="noopener noreferrer" style={{ '--accent': p.accent }}>
                  <span className="svc-project-tag">{p.tag}</span>
                  <h4 className="svc-project-name">{p.name}</h4>
                  <p className="svc-project-desc">{p.desc}</p>
                  <span className="svc-project-link">Visit Live Site <ArrowIcon /></span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ DESIGN CAPABILITY — three live concept builds ══════════ */}
      <section className="svc-section svc-section-alt">
        <div className="container">
          <Reveal variant="up">
            <div className="section-label">DESIGN CAPABILITY</div>
            <h2 className="svc-section-title">Beyond client work, here's the range we design across.</h2>
            <p className="svc-section-sub">
              Alongside the live projects above, here are three concept builds — real, rendered interfaces, not
              stock photos — showing how differently we'd approach a dashboard versus a storefront versus an app.
            </p>
          </Reveal>

          <div className="svc-concept-grid">
            {CONCEPTS.map(({ key, Comp, name, desc, variant }, i) => (
              <Reveal key={key} variant={i === 0 ? 'left' : i === 1 ? 'up' : 'right'} delay={i * 120}>
                <button className="svc-concept-card" onClick={() => setOpenConcept(key)} type="button">
                  <div className={`svc-concept-stage ${variant === 'phone' ? 'svc-concept-stage-phone' : ''}`}>
                    <div className="svc-concept-comp"><Comp /></div>
                    <div className="svc-concept-hover">
                      <span>View Full Design →</span>
                    </div>
                  </div>
                  <h4 className="svc-concept-name">{name}</h4>
                  <p className="svc-concept-desc">{desc}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {CONCEPTS.map(({ key, Comp, name, variant }) => (
        <ConceptModal key={key} open={openConcept === key} variant={variant} name={name} onClose={() => setOpenConcept(null)}>
          <Comp full />
        </ConceptModal>
      ))}

      {/* ══════════ PROCESS — image + numbered steps ══════════ */}
      <section className="svc-section svc-section-alt">
        <div className="container svc-process-grid">
          <Reveal variant="left">
            <ParallaxImage src={IMG_PROCESS} alt="Development process" speed={0.1} className="svc-process-img" />
          </Reveal>
          <div>
            <Reveal variant="right">
              <div className="section-label">HOW IT WORKS</div>
              <h2 className="svc-section-title">From idea to launch in four steps.</h2>
            </Reveal>
            <div className="svc-process-steps">
              {PROCESS.map((step, i) => (
                <Reveal key={step.num} variant="right" delay={i * 110} className="svc-process-step">
                  <span className="svc-process-num">{step.num}</span>
                  <div>
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ QUOTE FORM — split image + form ══════════ */}
      <section className="svc-section svc-quote-section" ref={formRef}>
        <div className="container">
          <Reveal variant="up">
            <div className="section-label">GET STARTED</div>
            <h2 className="svc-section-title">Tell us about your project.</h2>
            <p className="svc-section-sub">Fill this out and we'll get back to you within 24 hours with a free quote.</p>
          </Reveal>

          <div className="svc-quote-grid">
            <Reveal variant="left">
              <ParallaxImage src={IMG_TEAM} alt="Our development team" speed={0.08} className="svc-quote-img" />
            </Reveal>

            <Reveal variant="right" delay={80}>
              {submitted ? (
                <div className="svc-success">
                  <span style={{ fontSize: 40 }}>🎉</span>
                  <h3>Request received!</h3>
                  <p>Thanks for reaching out — our team will email you within 24 hours with a quote.</p>
                </div>
              ) : (
                <form className="svc-form" onSubmit={handleSubmit}>
                  <div className="svc-form-row">
                    <div className="svc-form-group">
                      <label>Project Type</label>
                      <select name="projectType" value={form.projectType} onChange={handleChange}>
                        <option>Website</option>
                        <option>Web Application</option>
                        <option>Mobile App</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div className="svc-form-group">
                      <label>Budget Range</label>
                      <select name="budget" value={form.budget} onChange={handleChange}>
                        <option value="">Select a range</option>
                        {BUDGET_OPTIONS.map(b => <option key={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="svc-form-group">
                    <label>Timeline</label>
                    <select name="timeline" value={form.timeline} onChange={handleChange}>
                      <option value="">Select a timeline</option>
                      {TIMELINE_OPTIONS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="svc-form-group">
                    <label>Describe Your Project *</label>
                    <textarea
                      name="description" value={form.description} onChange={handleChange} rows={4}
                      placeholder="What are you trying to build? Any features, references, or goals you have in mind…"
                      required
                    />
                  </div>

                  <div className="svc-form-row">
                    <div className="svc-form-group">
                      <label>Your Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} placeholder="Full name" required />
                    </div>
                    <div className="svc-form-group">
                      <label>Email *</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" required />
                    </div>
                  </div>
                  <div className="svc-form-group">
                    <label>Phone (optional)</label>
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit mobile number" />
                  </div>

                  <button type="submit" className="svc-btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={submitting}>
                    {submitting ? 'Sending...' : <>Get My Free Quote <ArrowIcon /></>}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ STYLES ═══════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .svc-page { background: var(--bg); color: var(--text); overflow-x: hidden; font-family: 'Plus Jakarta Sans', sans-serif; }
        .svc-page .container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

        /* ── Reveal variants ── */
        .svc-rv { opacity: 0; transition: opacity 0.8s ease, transform 0.8s ease; }
        .svc-rv-up { transform: translateY(36px); }
        .svc-rv-scale { transform: scale(0.85); }
        .svc-rv-left { transform: translateX(-60px); }
        .svc-rv-right { transform: translateX(60px); }
        .svc-rv-rotate { transform: rotate(-6deg) scale(0.9) translateY(20px); }
        .svc-rv-in { opacity: 1; transform: none; }

        /* ── HERO ── */
        .svc-hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; padding: 120px 24px 60px; background: #0a0614; }
        .svc-hero-bg { position: absolute; inset: -6%; background-size: cover; background-position: center 35%; will-change: transform; }
        .svc-hero-overlay { position: absolute; inset: 0; background: rgba(8,4,18,0.72); }
        .svc-hero-vignette { position: absolute; inset: 0; box-shadow: inset 0 -140px 170px -60px rgba(0,0,0,0.75), inset 0 120px 160px -60px rgba(0,0,0,0.35); pointer-events: none; }
        .svc-hero-divider { position: absolute; left: 0; right: 0; bottom: 0; height: 5px; background: ${V1}; z-index: 3; }
        .svc-hero-content { position: relative; z-index: 2; text-align: center; max-width: 760px; }
        .svc-hero-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: clamp(36px, 7vw, 66px); line-height: 1.06; letter-spacing: -0.03em; margin: 14px 0 22px; color: #fff; }
        .svc-hero-sub { font-size: clamp(15px, 2vw, 18px); color: rgba(255,255,255,0.82); line-height: 1.7; max-width: 560px; margin: 0 auto 36px; }
        .svc-scroll-hint { position: absolute; bottom: 34px; left: 50%; transform: translateX(-50%); z-index: 2; display: flex; flex-direction: column; align-items: center; gap: 8px; }
        .svc-scroll-hint span { font-size: 11px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(255,255,255,0.65); }
        .svc-scroll-line { width: 1px; height: 30px; background: ${V2}; animation: svcScrollPulse 1.8s ease-in-out infinite; }
        @keyframes svcScrollPulse { 0%,100% { opacity: 0.25; } 50% { opacity: 1; } }

        .svc-btn-primary {
          display: inline-flex; align-items: center; gap: 8px; padding: 15px 30px; border-radius: 10px; border: none; cursor: pointer;
          background: ${V1}; color: #fff; font-weight: 700; font-size: 15.5px;
          font-family: 'Plus Jakarta Sans', sans-serif; transition: transform 0.2s, background 0.2s;
        }
        .svc-btn-primary:hover { transform: translateY(-2px); background: ${V3}; }
        .svc-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .svc-btn-outline {
          display: inline-flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 10px;
          background: transparent; border: 1.5px solid ${V1}; color: ${V1}; font-weight: 700; font-size: 14px; cursor: pointer;
          font-family: 'Plus Jakarta Sans', sans-serif; margin-top: 6px; transition: all 0.2s;
        }
        .svc-btn-outline:hover { background: ${V1}; color: #fff; }

        /* ── SECTIONS ── */
        .svc-section { padding: 100px 0; position: relative; }
        .svc-section-alt { background: var(--bg2); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
        .svc-section-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: clamp(26px, 4vw, 38px); letter-spacing: -0.02em; margin: 10px 0 16px; max-width: 720px; color: var(--text); }
        .svc-section-sub { color: var(--muted); font-size: 15.5px; line-height: 1.7; max-width: 620px; margin-bottom: 20px; }

        /* ── PARALLAX IMAGE ── */
        .svc-pimg { overflow: hidden; border-radius: 16px; position: relative; }
        .svc-pimg img { width: 100%; height: 100%; object-fit: cover; display: block; will-change: transform; }

        /* ── SERVICE CARDS ── */
        .svc-cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; margin-top: 36px; }
        .svc-card { background: var(--card-solid); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; height: 100%; display: flex; flex-direction: column; box-shadow: var(--shadow-card); }
        .svc-card-img { height: 170px; border-radius: 0; }
        .svc-card-body { padding: 24px; display: flex; flex-direction: column; flex: 1; }
        .svc-card-body h3 { font-size: 19px; font-weight: 800; margin: 0 0 8px; }
        .svc-card-tagline { font-size: 13.5px; color: var(--muted); line-height: 1.6; margin-bottom: 16px; }
        .svc-deliverables { list-style: none; padding: 0; margin: 0 0 18px; display: flex; flex-direction: column; gap: 9px; flex: 1; }
        .svc-deliverables li { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--text-2); line-height: 1.45; }
        .svc-check { flex-shrink: 0; width: 16px; height: 16px; border-radius: 50%; background: ${V1}; display: flex; align-items: center; justify-content: center; color: #fff; margin-top: 2px; }

        /* ── WHY ROWS ── */
        .svc-why-rows { display: flex; flex-direction: column; gap: 70px; margin-top: 40px; }
        .svc-why-row { display: grid; grid-template-columns: 1fr 1fr; gap: 50px; align-items: center; }
        .svc-why-row-rev .svc-why-img { order: 2; }
        .svc-why-img { height: 280px; }
        .svc-why-num { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 800; color: ${V1}; letter-spacing: 0.05em; }
        .svc-why-text h3 { font-size: 24px; font-weight: 800; margin: 10px 0 12px; }
        .svc-why-text p { font-size: 15px; color: var(--muted); line-height: 1.7; }

        /* ── REAL PROJECT GRID ── */
        .svc-project-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 40px; }
        .svc-project-card {
          display: flex; flex-direction: column; background: var(--card-solid); border: 1px solid var(--border);
          border-radius: 16px; padding: 26px; text-decoration: none; color: inherit; box-shadow: var(--shadow-card);
          border-top: 3px solid var(--accent); transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .svc-project-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,0.12); }
        .svc-project-tag { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--accent); margin-bottom: 10px; }
        .svc-project-name { font-size: 19px; font-weight: 800; margin: 0 0 10px; }
        .svc-project-desc { font-size: 13.5px; color: var(--muted); line-height: 1.6; margin: 0 0 20px; flex: 1; }
        .svc-project-link { display: inline-flex; align-items: center; gap: 7px; font-size: 13.5px; font-weight: 700; color: var(--accent); }

        /* ── DESIGN CONCEPT GRID ── */
        .svc-concept-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; margin-top: 40px; align-items: start; }
        .svc-concept-card {
          display: flex; flex-direction: column; background: none; border: none; padding: 0;
          text-align: left; cursor: pointer; font-family: inherit; color: inherit; width: 100%;
        }
        .svc-concept-card:focus-visible { outline: 2px solid ${V1}; outline-offset: 4px; border-radius: 8px; }
        .svc-concept-stage { position: relative; height: 330px; margin-bottom: 16px; display: flex; }
        .svc-concept-stage-phone { justify-content: center; }
        .svc-concept-comp { width: 100%; height: 100%; }
        .svc-concept-stage-phone .svc-concept-comp { width: 220px; }
        .svc-concept-hover {
          position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
          background: rgba(10,6,20,0.72); opacity: 0; transition: opacity 0.25s ease; border-radius: 14px;
          pointer-events: none;
        }
        .svc-concept-card:hover .svc-concept-hover, .svc-concept-card:focus-visible .svc-concept-hover { opacity: 1; }
        .svc-concept-hover span { color: #fff; font-weight: 700; font-size: 14px; }
        .svc-concept-name { font-size: 17px; font-weight: 800; margin: 0 0 6px; }
        .svc-concept-desc { font-size: 13.5px; color: var(--muted); line-height: 1.6; margin: 0; }

        /* ── PROCESS ── */
        .svc-process-grid { display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 60px; align-items: center; }
        .svc-process-img { height: 420px; }
        .svc-process-steps { display: flex; flex-direction: column; gap: 26px; margin-top: 30px; }
        .svc-process-step { display: flex; align-items: flex-start; gap: 18px; }
        .svc-process-num { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 22px; color: ${V1}; flex-shrink: 0; width: 46px; }
        .svc-process-step h4 { font-size: 16.5px; font-weight: 800; margin: 0 0 6px; }
        .svc-process-step p { font-size: 13.5px; color: var(--muted); line-height: 1.6; margin: 0; }

        /* ── QUOTE FORM ── */
        .svc-quote-section { padding-bottom: 120px; }
        .svc-quote-grid { display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 40px; align-items: stretch; margin-top: 20px; }
        .svc-quote-img { height: 100%; min-height: 480px; }
        .svc-form { background: var(--card-solid); border: 1px solid var(--border); border-radius: 20px; padding: 32px; box-shadow: var(--shadow-md); }
        .svc-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
        .svc-form-group { display: flex; flex-direction: column; gap: 7px; margin-bottom: 16px; }
        .svc-form-group:last-child { margin-bottom: 0; }
        .svc-form-group label { font-size: 13px; font-weight: 700; color: var(--text); }
        .svc-form-group input, .svc-form-group select, .svc-form-group textarea {
          background: var(--bg3); border: 1.5px solid var(--border); border-radius: 10px; color: var(--text);
          padding: 12px 14px; font-size: 14.5px; font-family: 'Plus Jakarta Sans', sans-serif; outline: none;
          transition: border-color 0.2s; box-sizing: border-box; width: 100%;
        }
        .svc-form-group input:focus, .svc-form-group select:focus, .svc-form-group textarea:focus { border-color: ${V1}; }
        .svc-form-group textarea { resize: vertical; }

        .svc-success { text-align: center; padding: 60px 30px; background: var(--card-solid); border: 1px solid var(--border); border-radius: 20px; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
        .svc-success h3 { font-size: 22px; font-weight: 800; margin: 14px 0 8px; }
        .svc-success p { color: var(--muted); }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .svc-cards-grid { grid-template-columns: 1fr; }
          .svc-project-grid { grid-template-columns: 1fr; }
          .svc-concept-grid { grid-template-columns: 1fr; max-width: 380px; margin-left: auto; margin-right: auto; }
          .svc-why-row, .svc-why-row-rev { grid-template-columns: 1fr; }
          .svc-why-row-rev .svc-why-img { order: 0; }
          .svc-process-grid { grid-template-columns: 1fr; }
          .svc-process-img { height: 260px; }
          .svc-quote-grid { grid-template-columns: 1fr; }
          .svc-quote-img { min-height: 220px; }
          .svc-form-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default Services;
